/**
 * Ticket authorization policy: who may authorize starting work on a ticket.
 *
 * This decides only whether Auto Mode may begin the ticket. In normal chat a technician is
 * already directing the work, so these checks do not run. Per-action approvals while Theo
 * works are a separate mechanism.
 *
 * Own-account requests stay with the requester. Every other request type is approved by any
 * selected group: customer admins, customer HR, or a technician.
 */

export type RequestType =
  | "own_account"
  | "others_resource"
  | "admin_change"
  | "employee_lifecycle"
  | "service_impacting";

export type Authority = "admin" | "hr" | "technician";

/** Selected authorities, always in admin, hr, technician order. Any one of them is enough. */
export type AuthoritySet = readonly Authority[];

export interface ApprovalRules {
  others_resource: AuthoritySet;
  admin_change: AuthoritySet;
  employee_lifecycle: AuthoritySet;
  service_impacting: AuthoritySet;
}

export type ConfigurableRequestType = keyof ApprovalRules;

export type AuthorityPreset = "anyone" | "customer_approvers" | "with_technician";

type ApprovalPreset = Exclude<AuthorityPreset, "anyone">;

export interface AuthorizationPolicy {
  /** False means anyone at the customer can authorize work and Theo checks nothing before starting. */
  requireApproval: boolean;
  rules: Readonly<ApprovalRules>;
}

export interface Labeled<T extends string> {
  value: T;
  label: string;
  description?: string;
}

interface RowBase<T extends RequestType> {
  value: T;
  label: string;
  description: string;
}

export interface FixedRequestRow extends RowBase<"own_account"> {
  kind: "fixed";
  approver: string;
}

export interface ChoiceRequestRow<K extends ConfigurableRequestType = ConfigurableRequestType> extends RowBase<K> {
  kind: "choice";
}

export type RequestRow = FixedRequestRow | ChoiceRequestRow;

export const AUTHORITIES: readonly Labeled<Authority>[] = [
  { value: "admin", label: "Customer admins" },
  { value: "hr", label: "Customer HR" },
  { value: "technician", label: "Technician" },
];

export const REQUEST_ROWS: readonly RequestRow[] = [
  {
    kind: "fixed",
    value: "own_account",
    label: "Own account",
    description: "The requester's own password, MFA, phone, office location, or other profile details",
    approver: "The ticket requester, or the author of a note",
  },
  {
    kind: "choice",
    value: "others_resource",
    label: "Another person's account or resource",
    description: "Their mailbox, folder, device, VPN, or license, including a password reset for someone else",
  },
  {
    kind: "choice",
    value: "admin_change",
    label: "Administrative change",
    description: "Managing a list or group, assigning a license, suspending an account, buying a seat",
  },
  {
    kind: "choice",
    value: "employee_lifecycle",
    label: "Employee lifecycle",
    description: "Onboarding, offboarding, disable or re-enable, mailbox conversion, forwarding, leave reply",
  },
  {
    kind: "choice",
    value: "service_impacting",
    label: "Service-impacting change",
    description:
      "Shared-service restart, production restore, VM resize, firewall or tenant mail rule, conditional access, admin role, non-catalog driver, license purchase, disruptive reboot",
  },
];

const admin: AuthoritySet = ["admin"];
const hr: AuthoritySet = ["hr"];
const adminOrHr: AuthoritySet = ["admin", "hr"];
const adminOrTechnician: AuthoritySet = ["admin", "technician"];
const adminHrOrTechnician: AuthoritySet = ["admin", "hr", "technician"];

const PRESET_RULES: Readonly<Record<ApprovalPreset, Readonly<ApprovalRules>>> = {
  customer_approvers: {
    others_resource: admin,
    admin_change: admin,
    employee_lifecycle: hr,
    service_impacting: admin,
  },
  with_technician: {
    others_resource: adminOrTechnician,
    admin_change: adminHrOrTechnician,
    employee_lifecycle: adminOrHr,
    service_impacting: adminOrTechnician,
  },
};

export const AUTHORITY_PRESETS: readonly Labeled<AuthorityPreset>[] = [
  {
    value: "anyone",
    label: "Anyone can authorize",
    description: "Any contact at the customer can request work, and Theo starts without checking for approval.",
  },
  {
    value: "customer_approvers",
    label: "Customer approvers",
    description:
      "Customer admins approve changes to other people's accounts, administrative changes, and service-impacting work. HR approves employee lifecycle changes.",
  },
  {
    value: "with_technician",
    label: "Include a technician",
    description:
      "In Auto Mode, a technician's approval also counts. Another person's account: admins or a technician. Lifecycle: admins or HR. Administrative changes: admins, HR, or a technician.",
  },
];

/** Rules from the authorization spec that no MSP setting can turn off. */
export const FIXED_RULES: readonly string[] = [
  "In normal chat these checks do not run. A technician is already directing the work.",
  "In Auto Mode, Technician means a user of this MSP approved in Theo chat, is the ticket requester, or wrote the approval in a ticket note.",
  "Quoted, pasted, or forwarded approval text is not approval.",
  "Approval covers only the exact target, access level, and period it names.",
  "An expired or superseded approval does not cover a new request.",
];

export const DEFAULT_AUTHORIZATION_POLICY: AuthorizationPolicy = {
  requireApproval: true,
  rules: PRESET_RULES.customer_approvers,
};

function sameAuthorities(left: AuthoritySet, right: AuthoritySet): boolean {
  return left.length === right.length && left.every((authority, index) => authority === right[index]);
}

function sameRules(left: Readonly<ApprovalRules>, right: Readonly<ApprovalRules>): boolean {
  return (Object.keys(left) as ConfigurableRequestType[]).every((key) => sameAuthorities(left[key], right[key]));
}

/** Adds or removes one authority. Removing the last authority leaves the policy unchanged. */
export function toggleAuthority(policy: AuthorizationPolicy, requestType: ConfigurableRequestType, authority: Authority): AuthorizationPolicy {
  const current = policy.rules[requestType];
  const next = AUTHORITIES.map((option) => option.value).filter((option) =>
    option === authority ? !current.includes(option) : current.includes(option)
  );
  if (next.length === 0 || sameAuthorities(current, next)) return policy;
  return { ...policy, rules: { ...policy.rules, [requestType]: next } };
}

/** "anyone" turns approval off and keeps the rows, so switching back restores them. */
export function applyAuthorityPreset(policy: AuthorizationPolicy, preset: AuthorityPreset): AuthorizationPolicy {
  if (preset === "anyone") return { ...policy, requireApproval: false };
  return { ...policy, requireApproval: true, rules: PRESET_RULES[preset] };
}

/** The preset the policy matches, or "custom" when a row was changed by hand. */
export function deriveAuthorityPreset(policy: AuthorizationPolicy): AuthorityPreset | "custom" {
  if (!policy.requireApproval) return "anyone";
  const presets = Object.keys(PRESET_RULES) as ApprovalPreset[];
  const match = presets.find((preset) => sameRules(PRESET_RULES[preset], policy.rules));
  return match ?? "custom";
}
